/**
 * Synthesised sound effects.
 *
 * Ported from `L2-MECANICAS/src/engine/AudioSystem.ts`, unchanged in substance:
 * every sound is oscillators and noise buffers, so the repository stays free of
 * audio assets exactly as it is free of textures and meshes.
 *
 * Two fixes over the original: the nodes are stopped rather than left for the
 * collector, and every voice runs through one master gain so muting is
 * immediate rather than "no new sounds from now on".
 */

class AudioSystem {
    constructor() {
        /** @type {AudioContext|null} */
        this.ctx = null;
        /** @type {GainNode|null} */
        this.master = null;
        this.muted = false;
    }

    _init() {
        if (!this.ctx) {
            const Ctor = window.AudioContext ?? window.webkitAudioContext;
            if (!Ctor) return null;
            this.ctx = new Ctor();
            this.master = this.ctx.createGain();
            this.master.gain.value = this.muted ? 0 : 1;
            this.master.connect(this.ctx.destination);
        }
        // Browsers start the context suspended until a gesture; every sound here
        // follows one, so resuming on demand is enough.
        if (this.ctx.state === "suspended") this.ctx.resume();
        return this.ctx;
    }

    toggleMute() {
        this.muted = !this.muted;
        if (this.master) this.master.gain.value = this.muted ? 0 : 1;
        return this.muted;
    }

    getMuted() {
        return this.muted;
    }

    /** A blade landing: filtered noise burst plus a metallic ping. */
    playAttackHit(isCrit = false, isSoulshot = false) {
        if (this.muted) return;
        const ctx = this._init();
        if (!ctx) return;
        const now = ctx.currentTime;

        const len = Math.floor(ctx.sampleRate * 0.15);
        const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;

        const noise = ctx.createBufferSource();
        noise.buffer = buffer;

        const filter = ctx.createBiquadFilter();
        filter.type = "bandpass";
        filter.frequency.setValueAtTime(isCrit ? 2200 : 1200, now);
        filter.Q.setValueAtTime(3.0, now);

        const volume = isCrit ? 0.35 : 0.2;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(volume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

        noise.connect(filter);
        filter.connect(gain);
        gain.connect(this.master);
        noise.start(now);
        noise.stop(now + 0.16);

        const osc = ctx.createOscillator();
        const oscGain = ctx.createGain();
        osc.type = isCrit ? "sawtooth" : "triangle";
        osc.frequency.setValueAtTime(isCrit ? 440 : 280, now);
        osc.frequency.exponentialRampToValueAtTime(110, now + 0.12);
        oscGain.gain.setValueAtTime(volume * 0.8, now);
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        osc.connect(oscGain);
        oscGain.connect(this.master);
        osc.start(now);
        osc.stop(now + 0.13);

        if (isSoulshot) this.playSoulshotCrackle();
    }

    playSoulshotCrackle() {
        this._sweep("sine", 600, 1800, 0.18, 0.25);
    }

    playMagicCast(heal = false) {
        if (heal) this._sweep("sine", 523.25, 1046.5, 0.35, 0.25);
        else this._sweep("sine", 200, 800, 0.25, 0.25, 0.35);
    }

    /** The fanfare the original defined and never played. */
    playLevelUp() {
        if (this.muted) return;
        const ctx = this._init();
        if (!ctx) return;
        const now = ctx.currentTime;
        const notes = [523.25, 659.25, 783.99, 1046.5]; // C major arpeggio

        notes.forEach((freq, i) => {
            const at = now + i * 0.1;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "triangle";
            osc.frequency.setValueAtTime(freq, at);
            gain.gain.setValueAtTime(0.3, at);
            gain.gain.exponentialRampToValueAtTime(0.001, at + 0.3);
            osc.connect(gain);
            gain.connect(this.master);
            osc.start(at);
            osc.stop(at + 0.32);
        });
    }

    playUiClick() {
        this._sweep("sine", 800, 400, 0.04, 0.12);
    }

    /** A dull thud for taking a hit. */
    playHurt() {
        this._sweep("triangle", 180, 70, 0.22, 0.22);
    }

    /** One oscillator sweeping between two frequencies. */
    _sweep(type, from, to, seconds, volume, decay = seconds) {
        if (this.muted) return;
        const ctx = this._init();
        if (!ctx) return;
        const now = ctx.currentTime;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(from, now);
        osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), now + seconds);
        gain.gain.setValueAtTime(volume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + decay);
        osc.connect(gain);
        gain.connect(this.master);
        osc.start(now);
        osc.stop(now + decay + 0.02);
    }
}

export const audio = new AudioSystem();
